import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import MultiSelectAsyncV2 from '../MultiSelectAsyncV2';
import BaseService from 'services/BaseService';
import { buildLikeOption, isLikeOption, resolveLikeSelection } from '../nsirFilterLikeOption';

const DEFAULT_MIN_CHARS = 2;

/**
 * Async multi-select for NSIR filter facets (`GET nsirs/filter-options/{facet}`).
 * `like` prepends a "contém …" row that applies the scalar search instead of exact ids.
 * @see docs/iniciativas/filtros-nsir/contratos/nsirs-filter-options-api.md
 */
const NsirFilterFacetSelectV2 = ({
  facet,
  onSelect,
  value,
  label,
  disabled,
  minChars = DEFAULT_MIN_CHARS,
  scopeParams,
  like,
  likeValue = undefined,
  likeNoun,
}) => {
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const noun = likeNoun || label;

  useEffect(() => {
    let cancelled = false;
    const hasIds = value && Array.isArray(value) && value.length > 0 && typeof value[0] !== 'object';

    if (hasIds) {
      setLoadingInitial(true);
      const hydrateValues = async () => {
        try {
          const params = new URLSearchParams();
          params.append('values', value.join(','));
          const response = await BaseService.get({
            url: `nsirs/filter-options/${facet}/ids?${params.toString()}`,
          });
          const list = Array.isArray(response?.data) ? response.data : [];
          if (!cancelled) {
            setSelectedObjects(list.map((o) => ({ value: o.id, label: o.label || o.id })));
          }
        } catch (e) {
          if (!cancelled) {
            setSelectedObjects(value.map((id) => ({ value: id, label: String(id) })));
          }
        } finally {
          if (!cancelled) setLoadingInitial(false);
        }
      };
      hydrateValues();
      return () => {
        cancelled = true;
      };
    }

    if (like && likeValue) {
      setSelectedObjects([buildLikeOption(likeValue, noun)]);
      return undefined;
    }

    setSelectedObjects([]);
    return undefined;
  }, [value, facet, like, likeValue, noun]);

  const loadOptions = useCallback(async (inputValue) => {
    const trimmed = (inputValue || '').trim();
    const likeOption = like && trimmed.length >= minChars
      ? [buildLikeOption(trimmed, noun)]
      : [];
    if (!inputValue || inputValue.length < minChars) return [];
    try {
      const params = new URLSearchParams();
      params.append('search', inputValue);
      params.append('limit', '40');
      if (scopeParams?.tipoServicos?.length) {
        params.append('tipoServicos', scopeParams.tipoServicos.join(','));
      }
      if (scopeParams?.subTipoIds?.length) {
        params.append('subTipoIds', scopeParams.subTipoIds.join(','));
      }
      const response = await BaseService.get({
        url: `nsirs/filter-options/${facet}?${params.toString()}`,
      });
      const list = Array.isArray(response?.data) ? response.data : [];
      const options = list.map((o) => ({ value: o.id, label: o.label || o.id }));
      return [...likeOption, ...options];
    } catch (e) {
      console.error(`Erro ao buscar filter-options/${facet}:`, e);
      return likeOption;
    }
  }, [facet, minChars, scopeParams, like, noun]);

  const handleChange = (selected) => {
    const next = like
      ? resolveLikeSelection(selectedObjects, selected)
      : (selected || []);
    setSelectedObjects(next);
    const likeOpt = next.find(isLikeOption);
    if (likeOpt) {
      onSelect(null, likeOpt.likeQuery);
      return;
    }
    const ids = next.length ? next.map((v) => v.value) : null;
    onSelect(ids, null);
  };

  return (
    <MultiSelectAsyncV2
      label={label}
      disabled={disabled || loadingInitial}
      loadOptions={loadOptions}
      value={selectedObjects}
      placeholder={
        loadingInitial
          ? 'Carregando...'
          : `Digite ${minChars}+ caracteres para buscar`
      }
      onChange={handleChange}
    />
  );
};

NsirFilterFacetSelectV2.propTypes = {
  facet: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  value: PropTypes.array,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  minChars: PropTypes.number,
  scopeParams: PropTypes.shape({
    tipoServicos: PropTypes.array,
    subTipoIds: PropTypes.array,
  }),
  like: PropTypes.bool,
  likeValue: PropTypes.string,
  likeNoun: PropTypes.string,
};

NsirFilterFacetSelectV2.defaultProps = {
  value: null,
  disabled: false,
  minChars: DEFAULT_MIN_CHARS,
  scopeParams: undefined,
  like: false,
  likeValue: undefined,
  likeNoun: undefined,
};

export default NsirFilterFacetSelectV2;
